/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ICoreContext, getAsset } from '@msdyn365-commerce/core';
import { Button } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import classnames from 'classnames';

export interface ISearchFormProps {
    hydrated?: boolean;
    context: ICoreContext;
    disableSubmitSearch: boolean | undefined;
    searchTextInput: React.RefObject<HTMLInputElement>;
    maxChars: number;
    searchQsp: string;
    searchPlaceholder: string;
    query: string;
    ariaLabelCancel: string;
    ariaLabelSearch: string;
    ariaLabelSubmit: string;
    handleCancelSearchChange(): void;
    handleScannerButtonClick(): void;
    handleInputChange(query: string): void;
    handleInputFocus(): void;
}

export interface ISearchFormViewProps {
    input: React.ReactNode;
    submitBtn: React.ReactNode;
    cancelBtn: React.ReactNode;
    scannerBtn?: React.ReactNode;
}

/**
 * On Input Change functionality.
 * @param handleInputChange -Input change function.
 * @returns Set updated input value.
 */
const onInputChangeFunction = (handleInputChange: (query: string) => void) => (event: React.ChangeEvent<HTMLInputElement>) => {
    handleInputChange(event.target.value || '');
};
export const FormComponent = (props: ISearchFormProps): ISearchFormViewProps => {
    const {
        context,
        searchQsp,
        searchPlaceholder,
        searchTextInput,
        handleInputChange,
        handleInputFocus,
        maxChars,
        disableSubmitSearch,
        handleCancelSearchChange,
        handleScannerButtonClick,
        query,
        ariaLabelCancel,
        ariaLabelSearch,
        ariaLabelSubmit
    } = props;

    const onInputChange = onInputChangeFunction(handleInputChange);

    const inputNode = (
        <input
            className={classnames('ms-search__form-control')}
            type='text'
            aria-label={ariaLabelSearch}
            name={searchQsp}
            placeholder={searchPlaceholder}
            ref={searchTextInput}
            onChange={onInputChange}
            onFocus={handleInputFocus}
            maxLength={maxChars}
            value={query}
            disabled={!props.hydrated}
        />
    );

    const submitBtnNode = !disableSubmitSearch &&
        <Button className='ms-search__form-submitSearch' aria-label={ariaLabelSubmit}>
            <img src={getAsset('icons/search.svg', context.request)} style={{ filter: 'invert(1)' }} />
        </Button>
    ;

    const cancelBtNoden = (
        <Button
            className={!disableSubmitSearch ? 'ms-search__form-cancelSearch' : 'ms-search__form-cancelSearchNoSumbit'}
            aria-label={ariaLabelCancel}
            onClick={handleCancelSearchChange}
            type='button'
        />
    );

    const scannerBtnNode = (
        <Button
            className='ms-search__form-scanner'
            onClick={handleScannerButtonClick}
            type='button'
        >
            <img src={getAsset('icons/scanner.svg', context.request)} width={24} height={24} />
        </Button>
    );

    return { input: inputNode, cancelBtn: cancelBtNoden, submitBtn: submitBtnNode, scannerBtn: scannerBtnNode };
};
